import React, { useState, useEffect, useCallback } from 'react';
import type { Section, SectionRequest, Floor } from '../api/api';
import { getSectionsByFloor, createSection, updateSection, deleteSection } from '../api/api';
import Modal from './Modal';
import Button from '../ui/Button'; // Import custom Button

interface SectionListProps {
  selectedFloor: Floor | null;
  selectedSection: Section | null;
  onSelectSection: (section: Section | null) => void;
  sections: Section[];
  setSections: React.Dispatch<React.SetStateAction<Section[]>>;
  isLoadingInitial: boolean; // isLoading prop from parent for initial section load
  setError: React.Dispatch<React.SetStateAction<string | null>>;
}

const SectionListComponent: React.FC<SectionListProps> = ({
  selectedFloor,
  selectedSection,
  onSelectSection,
  sections,
  setSections,
  isLoadingInitial,
  setError,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentSectionData, setCurrentSectionData] = useState<Partial<SectionRequest & { id?: string }>>({});
  const [isEditing, setIsEditing] = useState(false);
  const [sectionToDelete, setSectionToDelete] = useState<Section | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const clearError = useCallback(() => setError(null), [setError]);

  const refreshSections = useCallback(async () => {
    if (!selectedFloor) return;
    try {
      const updatedSections = await getSectionsByFloor(selectedFloor.id as string);
      setSections(updatedSections);
      clearError();
    } catch (err) {
      console.error('Failed to refresh sections:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to refresh sections list.';
      setError(errorMessage);
    }
  }, [selectedFloor, setSections, clearError, setError]);

  const openAddModal = () => {
    if (!selectedFloor) return;
    setCurrentSectionData({ name: '', floorId: selectedFloor.id as string });
    setIsEditing(false);
    setIsModalOpen(true);
    clearError();
  };

  const openEditModal = (section: Section) => {
    setCurrentSectionData({ id: section.id, name: section.name, floorId: section.floorId as string });
    setIsEditing(true);
    setIsModalOpen(true);
    clearError();
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentSectionData({});
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCurrentSectionData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFloor) {
      setError("No floor selected to add the section to.");
      return;
    }
    if (!currentSectionData.name?.trim()) {
      setError("Section name cannot be empty.");
      return;
    }
    setIsSubmitting(true);
    setError(null);

    // Ensure floorId is part of the payload for both create and update
    const payload: SectionRequest = {
      name: currentSectionData.name,
      floorId: currentSectionData.floorId || (selectedFloor.id as string)
    };

    try {
      if (isEditing && currentSectionData.id) {
        await updateSection(currentSectionData.id as string, payload);
      } else {
        await createSection(payload);
      }
      await refreshSections();
      closeModal();
    } catch (err) {
      console.error('Failed to save section:', err);
      const errorMessage = err instanceof Error ? err.message : 
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Failed to save section.';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const openDeleteConfirm = (section: Section) => {
    setSectionToDelete(section);
    setShowDeleteConfirm(true);
    clearError();
  };

  const closeDeleteConfirm = () => {
    setSectionToDelete(null);
    setShowDeleteConfirm(false);
  };

  const handleDeleteSection = async () => {
    if (!sectionToDelete || !selectedFloor) return;
    setIsDeleting(true);
    setError(null);
    try {
      await deleteSection(sectionToDelete.id as string);
      if (selectedSection?.id === sectionToDelete.id) {
        onSelectSection(null);
      }
      await refreshSections();
      closeDeleteConfirm();
    } catch (err) {
      console.error('Failed to delete section:', err);
      const errorMessage = err instanceof Error ? err.message : 
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Failed to delete section.';
      setError(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  // Effect to clear sections if selected floor is removed
  useEffect(() => {
    if (!selectedFloor) {
      setSections([]);
      onSelectSection(null);
    }
    // No need to fetch here, ManageLayoutPage's useEffect for selectedFloor handles it
  }, [selectedFloor, setSections, onSelectSection]);


  if (!selectedFloor) {
    return <p className="text-center text-[var(--color-text-secondary)] py-4">Select a floor to manage sections.</p>;
  }

  if (isLoadingInitial) {
    return <p className="text-center text-[var(--color-text-secondary)] py-4">Loading sections for {selectedFloor.name}...</p>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={openAddModal} variant="primary" size="small" disabled={!selectedFloor}>
          Add Section
        </Button>
      </div>

      {sections.length === 0 && !isLoadingInitial && (
        <p className="text-center text-[var(--color-text-secondary)] py-4">No sections found. Add one!</p>
      )}

      <ul className="space-y-2">
        {sections.map(section => (
          <li
            key={section.id}
            onClick={() => onSelectSection(section)}
            className={`p-3 rounded-md cursor-pointer transition-colors
                        ${selectedSection?.id === section.id
                            ? 'bg-[var(--color-primary-accent)] text-[var(--color-text-on-accent)] shadow-md'
                            : 'bg-[var(--color-background-default)] hover:bg-[var(--color-background-hover)] shadow'
                        }
                        border border-[var(--color-border-default)] flex justify-between items-center group`}
          >
            <span className="font-medium">{section.name}</span>
            <div className="opacity-0 group-hover:opacity-100 transition-opacity space-x-1">
              <Button
                onClick={(e) => { e.stopPropagation(); openEditModal(section); }}
                variant="secondary"
                size="small"
              >
                Edit
              </Button>
              <Button
                onClick={(e) => { e.stopPropagation(); openDeleteConfirm(section); }}
                variant="secondary"
                size="small"
              >
                Del
              </Button>
            </div>
          </li>
        ))}
      </ul>

      <Modal isOpen={isModalOpen} onClose={closeModal} title={isEditing ? 'Edit Section' : 'Add New Section'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="sectionName" className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
              Section Name:
            </label>
            <input
              type="text"
              id="sectionName"
              name="name"
              value={currentSectionData.name || ''}
              onChange={handleInputChange}
              required
              className="w-full p-2 border border-[var(--color-border-default)] rounded-md
                         focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)]
                         bg-[var(--color-background-input)] text-[var(--color-text-input)]"
              disabled={isSubmitting}
            />
          </div>
          <div className="flex justify-end space-x-3 pt-2">
            <Button type="button" onClick={closeModal} variant="secondary" disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={isSubmitting || !selectedFloor}>
              {isSubmitting ? (isEditing ? 'Saving...' : 'Adding...') : (isEditing ? 'Save Changes' : 'Add Section')}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={showDeleteConfirm} onClose={closeDeleteConfirm} title="Confirm Delete">
        <p className="mb-4 text-[var(--color-text-primary)]">Are you sure you want to delete section "<strong>{sectionToDelete?.name}</strong>"?</p>
        <p className="text-sm text-red-600 mb-4">This may also delete associated tables.</p>
        <div className="flex justify-end space-x-3">
          <Button type="button" onClick={closeDeleteConfirm} variant="secondary" disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteSection}
            variant="primary" // Consider 'danger' variant
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default SectionListComponent;
